import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { deleteAllInnovaAlgoTradesIntradayAlgoInnovaAlgoTradesDelete as deleteAllInnovaAlgoTrades } from "@/client-admin/services.gen";

interface DeleteAllInnovaAlgoTradesDialogProps {
  canDelete: boolean;
}

export function DeleteAllInnovaAlgoTradesDialog({ canDelete }: DeleteAllInnovaAlgoTradesDialogProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const { mutateAsync: deleteTradesMutation, isPending } = useMutation({
    mutationFn: deleteAllInnovaAlgoTrades,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ predicate: (q) => (q.queryKey[0] as string)?.startsWith("intraday-algo") });
      toast({
        title: "Trades deleted",
        description:
          `Cancelled ${data.cancelled} innova algo trade(s), ` +
          `reset ${data.signals_reset} signal(s) to Algo=0, ` +
          `cancelled ${data.etpa_orders_cancelled_by_intraday_code} ETPA order(s) by intraday code.`,
        duration: Infinity,
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.body?.detail ?? "Failed to delete trades. Please try again later.",
        variant: "destructive",
      });
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="destructive"
          disabled={!canDelete}
          title={canDelete ? undefined : "Pause the worker before deleting trades"}
        >
          <Trash2 className="mr-1 h-4 w-4" />
          Delete All Innova Algo Trades
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete All Innova Algo Trades</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              <p>This will:</p>
              <ol className="ml-4 list-decimal space-y-1">
                <li>
                  Cancel ETPA orders for active innova algo trades and mark all pending/active innova algo trades for
                  today and later delivery dates as <strong>CANCELLED</strong>.
                </li>
                <li>
                  Cancel any remaining ETPA orders with Innova intraday code (34). Orders found on ETPA but not in our
                  DB will be logged as warnings.
                </li>
                <li>Reset all signals with Algo=2 and delivery date today or later back to Algo=0.</li>
              </ol>
              <p>Are you sure?</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={async () => {
              await deleteTradesMutation();
              setOpen(false);
            }}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Confirm Delete"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
